import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { AIShowUser } from '@/components/ai-chat/types';
import { BUSINESS_SECTION_ALIASES } from '@/lib/constants/dashboard';

interface ShowUserState {
  active: boolean;
  section?: string;
  element?: string;
  employee?: string;
  shift?: string;
  message?: string;
}

interface ShowUserContextValue {
  state: ShowUserState;
  show: (instruction: AIShowUser) => void;
  dismiss: () => void;
  isHighlighted: (elementId: string) => boolean;
}

// Setter function that can be registered by DashboardTabContext
type TabSetter = (tab: string) => void;
let registeredTabSetter: TabSetter | null = null;

export function registerDashboardTabSetter(setter: TabSetter) {
  registeredTabSetter = setter;
}

export function unregisterDashboardTabSetter() {
  registeredTabSetter = null;
}


const ShowUserContext = createContext<ShowUserContextValue | null>(null);

const AUTO_DISMISS_MS = 8000; // Auto-dismiss highlight after 8 seconds
const ELEMENT_SEARCH_DELAY_MS = 300; // Delay before searching for elements (allow tab to render)

// Landing page sections that exist on the home view (not pricing)
const LANDING_PAGE_SECTIONS = new Set(['hero', 'features', 'how-it-works', 'contact']);

// CSS class for highlighting elements
const HIGHLIGHT_CLASS = 'ring-2 ring-primary ring-offset-2 animate-pulse';

/**
 * True for an app path like "/pricing". Rejects "javascript:" and other schemes, "//host",
 * and "/\host" or paths with whitespace (browsers read "\" as "/" and drop tabs/newlines).
 */
export function isSafeRoute(route: string): boolean {
  return /^\/(?![/\\])[^\\\s]*$/.test(route);
}

/**
 * Find an element by its showable ID using various strategies
 */
function findShowableElement(elementId: string): HTMLElement | null {
  const normalizedId = elementId.toLowerCase().replace(/\s+/g, '-');

  // Strategy 1: Find by data-showable attribute (exact match)
  let el = document.querySelector<HTMLElement>(`[data-showable="${elementId}"]`);
  if (el) return el;

  // Strategy 2: Find by data-showable attribute (normalized)
  el = document.querySelector<HTMLElement>(`[data-showable="${normalizedId}"]`);
  if (el) return el;

  // Strategy 3: Find by ID (exact)
  el = document.getElementById(elementId);
  if (el) return el;

  // Strategy 4: Find by ID (normalized)
  el = document.getElementById(normalizedId);
  if (el) return el;

  // Strategy 5: Find by aria-label containing the text
  el = document.querySelector<HTMLElement>(`[aria-label*="${elementId}" i]`);
  if (el) return el;

  // Strategy 6: Find by button/link text content (case-insensitive)
  const buttons = document.querySelectorAll<HTMLElement>('button, a, [role="button"], [role="tab"]');
  for (const btn of buttons) {
    const text = btn.textContent?.toLowerCase() || '';
    if (text.includes(elementId.toLowerCase())) {
      return btn;
    }
  }

  return null;
}

// Section → tab mapping uses BUSINESS_SECTION_ALIASES from constants.
// Cross-dashboard navigation is handled by AIMessage using React Router.

export function ShowUserProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ShowUserState>({ active: false });
  const dismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightedElementRef = useRef<HTMLElement | null>(null);

  // Clean up highlight from previous element
  const clearHighlight = useCallback(() => {
    if (highlightedElementRef.current) {
      HIGHLIGHT_CLASS.split(' ').forEach(cls => {
        highlightedElementRef.current?.classList.remove(cls);
      });
      highlightedElementRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearHighlight();
    setState({ active: false });
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
  }, [clearHighlight]);

  const show = useCallback((instruction: AIShowUser) => {
    // Clear any existing timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
    }

    // Handle route navigation (e.g., /pricing). The route comes from the AI, which reads
    // untrusted text (reviews), so only same-origin app paths are followed.
    if (instruction.route) {
      // shortcut: full page load because this provider sits outside <BrowserRouter> (App.tsx), move it inside to use useNavigate
      if (isSafeRoute(instruction.route)) window.location.assign(instruction.route);
      return;
    }

    // Navigate to section if specified
    // Note: Cross-dashboard navigation (e.g., to employee-dashboard) is handled by AIMessage
    // using React Router. This context only handles same-dashboard tab switching and landing page scrolling.
    if (instruction.section) {
      const sectionLower = instruction.section.toLowerCase();
      const mappedTab = BUSINESS_SECTION_ALIASES[sectionLower];

      // Try dashboard tab navigation
      if (mappedTab && registeredTabSetter) {
        registeredTabSetter(mappedTab);
      } else {
        // For landing page sections, navigate to home page first if needed
        const isLandingPageSection = LANDING_PAGE_SECTIONS.has(sectionLower);
        if (isLandingPageSection && window.location.pathname !== '/') {
          window.location.href = '/';
          // Scrolling will need to happen after navigation - best effort
          setTimeout(() => {
            const sectionElement = document.getElementById(instruction.section!);
            if (sectionElement) {
              sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 500);
        } else {
          // Direct scroll if already on the right page
          const sectionElement = document.getElementById(instruction.section);
          if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    }

    setState({
      active: true,
      section: instruction.section,
      element: instruction.element,
      employee: instruction.employee,
      shift: instruction.shift,
      message: instruction.message,
    });

    // Clear any previous highlight
    clearHighlight();

    // If element is specified, find and highlight it after a delay (allow tab to render)
    if (instruction.element) {
      setTimeout(() => {
        const el = findShowableElement(instruction.element!);
        if (el) {
          // Scroll into view
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Add highlight classes
          HIGHLIGHT_CLASS.split(' ').forEach(cls => el.classList.add(cls));
          highlightedElementRef.current = el;

          console.log('[ShowUserContext] Highlighted element:', instruction.element, el);
        } else {
          console.log('[ShowUserContext] Element not found:', instruction.element);
        }
      }, ELEMENT_SEARCH_DELAY_MS);
    }

    // Auto-dismiss after timeout
    dismissTimeoutRef.current = setTimeout(() => {
      dismiss();
    }, AUTO_DISMISS_MS);
  }, [dismiss, clearHighlight]);

  const isHighlighted = useCallback((elementId: string) => {
    if (!state.active) return false;

    // Normalize element ID for comparison
    const normalizedTarget = state.element?.toLowerCase().replace(/\s+/g, '-');
    const normalizedId = elementId.toLowerCase().replace(/\s+/g, '-');

    return normalizedTarget === normalizedId;
  }, [state.active, state.element]);

  // Listen for ai-show-user events from useAIChat
  useEffect(() => {
    const handleShowUser = (event: CustomEvent<AIShowUser>) => {
      show(event.detail);
    };

    window.addEventListener('ai-show-user', handleShowUser as EventListener);
    return () => {
      window.removeEventListener('ai-show-user', handleShowUser as EventListener);
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
    };
  }, [show]);

  return (
    <ShowUserContext.Provider value={{ state, show, dismiss, isHighlighted }}>
      {children}
    </ShowUserContext.Provider>
  );
}

export function useShowUser() {
  const context = useContext(ShowUserContext);
  if (!context) {
    throw new Error('useShowUser must be used within ShowUserProvider');
  }
  return context;
}

/**
 * Hook for components to register as highlightable
 * Returns props to spread on the element
 */
export function useHighlightable(elementId: string) {
  const { isHighlighted } = useShowUser();
  const highlighted = isHighlighted(elementId);

  return {
    'data-showable': elementId,
    'data-highlighted': highlighted ? 'true' : undefined,
    className: highlighted ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : '',
  };
}
